import { colors } from '../styles/theme';

export type StateColorConfig = {
  /** Foreground text color */
  color: string;
  /** Chip/component background color */
  bgColor: string;
};

/**
 * Parse a raw character state string into its logical components.
 *
 * Bot wire formats:
 *   PoE1:  "MapRunner\n( AP: 42 )"       → baseState: "MapRunner", atlasPoints: 42, build: null
 *   PoE2:  "Mapping"                      → baseState: "Mapping",  atlasPoints: null, build: null
 *   PoE2:  "Mapping\n Build:LightningArrow" → baseState: "Mapping",  atlasPoints: null, build: "LightningArrow"
 *
 * null / undefined / empty / whitespace → baseState: "" (lookup falls back to grey).
 */
export function parseState(raw: string | null | undefined): {
  baseState: string;
  atlasPoints: number | null;
  build: string | null;
} {
  if (!raw) return { baseState: '', atlasPoints: null, build: null };

  const trimmed = raw.trim();
  if (!trimmed) return { baseState: '', atlasPoints: null, build: null };

  const lines = trimmed.split('\n');
  const baseState = lines[0].trim();

  let atlasPoints: number | null = null;
  let build: string | null = null;

  if (lines.length > 1) {
    const suffix = lines.slice(1).join('\n').trim();

    // PoE1: "( AP: <N> )"
    const apMatch = suffix.match(/\(\s*AP:\s*(\d+)\s*\)/);
    if (apMatch) {
      atlasPoints = parseInt(apMatch[1], 10);
    }

    // PoE2 botfarm: "Build:<BuildName>"
    const buildMatch = suffix.match(/^Build:\s*(.+)/i);
    if (buildMatch) {
      build = buildMatch[1].trim();
    }
  }

  return { baseState, atlasPoints, build };
}

const STATE_COLORS: Record<string, StateColorConfig> = {
  // Green — Map running
  MapRunner:            { color: colors.successGreen, bgColor: 'rgba(39, 174, 96, 0.15)' },
  MirageInsideLogic:    { color: colors.successGreen, bgColor: 'rgba(39, 174, 96, 0.15)' },
  AlchAndGo:            { color: colors.successGreen, bgColor: 'rgba(39, 174, 96, 0.15)' },
  SplinterRunner:       { color: colors.successGreen, bgColor: 'rgba(39, 174, 96, 0.15)' },
  CitadelRusher:        { color: colors.successGreen, bgColor: 'rgba(39, 174, 96, 0.15)' },
  CorruptedNexusRusher: { color: colors.successGreen, bgColor: 'rgba(39, 174, 96, 0.15)' },
  JadeIslesRusher:      { color: colors.successGreen, bgColor: 'rgba(39, 174, 96, 0.15)' },
  CleansedMapFarmer:    { color: colors.successGreen, bgColor: 'rgba(39, 174, 96, 0.15)' },
  ManualMapClearer:     { color: colors.successGreen, bgColor: 'rgba(39, 174, 96, 0.15)' },
  StonesOfSerleFarmer:  { color: colors.successGreen, bgColor: 'rgba(39, 174, 96, 0.15)' },

  // Blue — Town/Hideout
  Hideout:              { color: colors.blueAccent, bgColor: 'rgba(40, 116, 166, 0.15)' },
  Townrun:              { color: colors.blueAccent, bgColor: 'rgba(40, 116, 166, 0.15)' },

  // Purple — Endgame/league
  SimulacrumRunner:     { color: '#9B59B6', bgColor: 'rgba(155, 89, 182, 0.15)' },
  BossRunner:           { color: '#9B59B6', bgColor: 'rgba(155, 89, 182, 0.15)' },
  AbyssLogic:           { color: '#9B59B6', bgColor: 'rgba(155, 89, 182, 0.15)' },
  LegionLogic:          { color: '#9B59B6', bgColor: 'rgba(155, 89, 182, 0.15)' },
  HarvestLogic:         { color: '#9B59B6', bgColor: 'rgba(155, 89, 182, 0.15)' },
  ExpeditionLogic:      { color: '#9B59B6', bgColor: 'rgba(155, 89, 182, 0.15)' },
  KingsmarchLogic:      { color: '#9B59B6', bgColor: 'rgba(155, 89, 182, 0.15)' },
  AfflictionLogic:      { color: '#9B59B6', bgColor: 'rgba(155, 89, 182, 0.15)' },
  MissionVaultLogic:    { color: '#9B59B6', bgColor: 'rgba(155, 89, 182, 0.15)' },
  VaalSideAreaLogic:    { color: '#9B59B6', bgColor: 'rgba(155, 89, 182, 0.15)' },
  ChaosRunner:          { color: '#9B59B6', bgColor: 'rgba(155, 89, 182, 0.15)' },
  SekhemasRunner:       { color: '#9B59B6', bgColor: 'rgba(155, 89, 182, 0.15)' },
  'Trial Logic':        { color: '#9B59B6', bgColor: 'rgba(155, 89, 182, 0.15)' },
  BestiaryLogic:        { color: '#9B59B6', bgColor: 'rgba(155, 89, 182, 0.15)' },
  BlightLogic:          { color: '#9B59B6', bgColor: 'rgba(155, 89, 182, 0.15)' },
  BrequelLogic:         { color: '#9B59B6', bgColor: 'rgba(155, 89, 182, 0.15)' },
  MirageExitPortalLogic:{ color: '#9B59B6', bgColor: 'rgba(155, 89, 182, 0.15)' },
  MirageClearDjinnLogic:{ color: '#9B59B6', bgColor: 'rgba(155, 89, 182, 0.15)' },
  MirageVarashtaTalkLogic:{ color: '#9B59B6', bgColor: 'rgba(155, 89, 182, 0.15)' },
  MirageWishSelectLogic:{ color: '#9B59B6', bgColor: 'rgba(155, 89, 182, 0.15)' },
  SummonAilithLogic:    { color: '#9B59B6', bgColor: 'rgba(155, 89, 182, 0.15)' },
  StrongboxLogic:       { color: '#9B59B6', bgColor: 'rgba(155, 89, 182, 0.15)' },
  StrongboxRollingLogic:{ color: '#9B59B6', bgColor: 'rgba(155, 89, 182, 0.15)' },
  BossingLogic:         { color: '#9B59B6', bgColor: 'rgba(155, 89, 182, 0.15)' },

  // Teal — Campaign/leveling
  Quester:              { color: '#1ABC9C', bgColor: 'rgba(26, 188, 156, 0.15)' },
  BloodAqueductRunner:  { color: '#1ABC9C', bgColor: 'rgba(26, 188, 156, 0.15)' },

  // Yellow — Multiplayer
  Follower:             { color: colors.warningAmber, bgColor: 'rgba(243, 156, 18, 0.15)' },
  Leader:               { color: colors.warningAmber, bgColor: 'rgba(243, 156, 18, 0.15)' },

  // Grey — Idle/paused
  'Empty Logic':        { color: colors.mediumGray, bgColor: 'rgba(136, 136, 136, 0.15)' },
  Dodger:               { color: colors.mediumGray, bgColor: 'rgba(136, 136, 136, 0.15)' },
  'Pause Logic':        { color: colors.mediumGray, bgColor: 'rgba(136, 136, 136, 0.15)' },
  PauseLogic:           { color: colors.mediumGray, bgColor: 'rgba(136, 136, 136, 0.15)' },
  TradeBuyHelper:       { color: colors.mediumGray, bgColor: 'rgba(136, 136, 136, 0.15)' },

  // Red — Problem signal
  Stopped:              { color: colors.primaryRed, bgColor: 'rgba(220, 47, 56, 0.18)' },
};

const FALLBACK: StateColorConfig = {
  color: colors.mediumGray,
  bgColor: 'rgba(136, 136, 136, 0.15)',
};

/**
 * Returns color config for a character state string.
 * Strips PoE1 AP suffix and PoE2 botfarm build suffix before lookup.
 * null/undefined/empty/unknown → neutral grey fallback.
 */
export function getStateColorConfig(state: string | null | undefined): StateColorConfig {
  const { baseState } = parseState(state);
  if (!baseState) return FALLBACK;
  return STATE_COLORS[baseState] ?? FALLBACK;
}
